/**
 * The first-visit intro tour on the web (ISSUE-1406), the phone's
 * `apps/mobile/src/welcome/IntroTour.tsx` and v1's onboarding_screen.dart:
 * three pages (live scores, pools, chat), each a large glyph in a soft circle
 * of the page's color. Skip sits top right until the last page, dots show
 * where the visitor is and jump to a page, Next becomes Get started, and "I
 * already have an account" is pinned on every page so a returning fan never
 * has to page through. Every way out calls `onDone`, which the route turns
 * into "seen" plus sign-in.
 *
 * One page is in the document at a time, so there is exactly one heading to
 * find and the polite live region reads the new page when it changes. The
 * keyed page slides in from the side it came from with `@starting-style`
 * (200ms, ease-enter), and `motion-reduce` drops the slide (ui-motion.mdc).
 *
 * Keys: the right and left arrows page, Escape skips. Touch: a sideways swipe
 * pages, the same rule the phone's pager follows.
 */
import { Button, cn, FOCUS_RING } from "@sportsmet/ui";
import {
  type ReactNode,
  type TouchEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  ChatDotsIcon,
  ScoreboardIcon,
  TrophyIcon,
} from "../../shell/SectionIcon";
import {
  clampIntroPage,
  INTRO_GET_STARTED_LABEL,
  INTRO_NEXT_LABEL,
  INTRO_PAGES,
  INTRO_SIGN_IN_LABEL,
  INTRO_SKIP_LABEL,
  INTRO_TOUR_LABEL,
  introPageAt,
  introProgressLabel,
  introSwipeTarget,
  isLastIntroPage,
  type typeIntroPage,
  type typeIntroTone,
} from "./introTourModel";

const TONE_CLASSES: Record<typeIntroTone, { circle: string; dot: string }> = {
  primary: { circle: "bg-primary-tint text-primary", dot: "bg-primary" },
  accent: { circle: "bg-accent-tint text-accent", dot: "bg-accent" },
  success: { circle: "bg-success-tint text-success", dot: "bg-success" },
};

const GLYPH_CLASS = "size-[72px]";

const PageGlyph = ({
  pageKey,
}: {
  pageKey: typeIntroPage["key"];
}): ReactNode => {
  switch (pageKey) {
    case "scores":
      return <ScoreboardIcon className={GLYPH_CLASS} />;
    case "pools":
      return <TrophyIcon className={GLYPH_CLASS} />;
    case "chat":
      return <ChatDotsIcon className={GLYPH_CLASS} />;
  }
};

/** A key press inside a text field belongs to the field, never the tour. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

export const IntroTour = ({ onDone }: { onDone: () => void }): ReactNode => {
  const [index, setIndex] = useState(0);
  const [isBack, setIsBack] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const page = introPageAt(index);
  const isLast = isLastIntroPage(index);

  const goTo = (next: number): void => {
    const target = clampIntroPage(next);
    if (target === index) return;
    setIsBack(target < index);
    setIndex(target);
  };

  // The latest `goTo` and `onDone` for the window listener, so it is added
  // once rather than on every page change.
  const handlers = useRef({ goTo, onDone, index });
  handlers.current = { goTo, onDone, index };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }
      if (isTypingTarget(event.target)) return;
      const current = handlers.current;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        current.goTo(current.index + 1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        current.goTo(current.index - 1);
      } else if (event.key === "Escape") {
        event.preventDefault();
        current.onDone();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const onTouchStart = (event: TouchEvent<HTMLDivElement>): void => {
    const touch = event.touches[0];
    touchStart.current =
      touch === undefined ? null : { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>): void => {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = event.changedTouches[0];
    if (start === null || touch === undefined) return;
    const target = introSwipeTarget({
      index,
      deltaX: touch.clientX - start.x,
      deltaY: touch.clientY - start.y,
    });
    if (target !== null) goTo(target);
  };

  const tone = TONE_CLASSES[page.tone];

  return (
    <section
      aria-label={INTRO_TOUR_LABEL}
      data-testid="welcome-screen"
      className="mx-auto flex min-h-[calc(100dvh-12rem)] w-full max-w-[420px] flex-col gap-4"
    >
      <div className="flex min-h-touch-target items-center justify-end">
        {isLast ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDone}
            data-testid="intro-skip"
          >
            {INTRO_SKIP_LABEL}
          </Button>
        )}
      </div>

      <div
        className="flex flex-1 flex-col items-center justify-center overflow-hidden py-4"
        aria-live="polite"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="intro-pager"
      >
        <div
          key={page.key}
          data-testid={`intro-page-${page.key}`}
          className={cn(
            "flex flex-col items-center gap-6 px-2 text-center transition duration-200 ease-enter motion-reduce:transition-none starting:opacity-0",
            isBack ? "starting:-translate-x-8" : "starting:translate-x-8",
          )}
        >
          <div
            aria-hidden="true"
            className={cn(
              "flex size-40 items-center justify-center rounded-full",
              tone.circle,
            )}
          >
            <PageGlyph pageKey={page.key} />
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="text-display text-ink">{page.title}</h1>
            <p className="text-body text-ink-secondary">{page.body}</p>
          </div>
        </div>
      </div>

      <div
        role="group"
        aria-label={introProgressLabel(index)}
        className="flex items-center justify-center"
        data-testid="intro-dots"
      >
        {INTRO_PAGES.map((dotPage, dot) => {
          const isCurrent = dot === index;
          return (
            <button
              key={dotPage.key}
              type="button"
              aria-label={`${introProgressLabel(dot)}: ${dotPage.title}`}
              aria-current={isCurrent ? "step" : undefined}
              onClick={() => {
                goTo(dot);
              }}
              className={cn(
                "flex size-touch-target items-center justify-center rounded-full",
                FOCUS_RING,
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "block h-2 rounded-full transition-[width,background-color] duration-200 ease-standard motion-reduce:transition-none",
                  isCurrent ? cn("w-6", tone.dot) : "w-2 bg-line",
                )}
              />
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-2" data-testid="intro-actions">
        <Button
          fullWidth
          onClick={() => {
            if (isLast) onDone();
            else goTo(index + 1);
          }}
          data-testid="intro-next"
        >
          {isLast ? INTRO_GET_STARTED_LABEL : INTRO_NEXT_LABEL}
        </Button>
        <Button
          variant="ghost"
          fullWidth
          onClick={onDone}
          data-testid="intro-sign-in"
        >
          {INTRO_SIGN_IN_LABEL}
        </Button>
      </div>
    </section>
  );
};
